import { normalizePostal, validPostal, displayPostal, dispatchEstimate, deliveryEstimate, formatDate } from './ab-delivery-calendar.js';

let routePromise;
const loadRoutes = () => routePromise ||= fetch(new URL('./ab-delivery-routes.json', import.meta.url), { credentials: 'omit' }).then(response => { if (!response.ok) throw new Error('Route data unavailable'); return response.json(); }).catch(error => { routePromise = null; throw error; });
const storageKey = 'atoms-bio-delivery-postal';
const storedPostal = () => { try { return sessionStorage.getItem(storageKey) || ''; } catch { return ''; } };
const rememberPostal = value => { try { sessionStorage.setItem(storageKey, value); } catch {} };
const replace = (text, values) => text.replace(/\[([a-z_]+)\]/g, (_, key) => values[key] ?? '');

class ABDeliveryDate extends HTMLElement {
  async connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;
    this.copy = JSON.parse(this.querySelector('[data-delivery-copy]').textContent);
    this.variants = JSON.parse(this.querySelector('[data-delivery-variants]')?.textContent || 'null');
    this.variantId = this.dataset.selectedVariant;
    this.locale = this.dataset.locale || 'en';
    this.form = this.querySelector('[data-postal-form]');
    this.input = this.form.elements.delivery_postal;
    this.arrival = this.querySelector('[data-arrival]');
    this.prompt = this.querySelector('[data-prompt]');
    this.dispatch = this.querySelector('[data-dispatch]');
    this.feedback = this.querySelector('[data-feedback]');
    this.location = this.querySelector('[data-location]');
    this.previewLabel = this.querySelector('[data-preview-label]');
    this.preview = this.dataset.preview === 'true' && !this.dataset.endpoint;
    this.form.hidden = false;
    this.controller = new AbortController();
    const options = { signal: this.controller.signal };
    this.form.addEventListener('submit', event => {
      event.preventDefault();
      this.postal = normalizePostal(this.input.value);
      if (!validPostal(this.postal)) { this.showFeedback(this.copy.invalid); this.input.setAttribute('aria-invalid', 'true'); this.input.focus(); return; }
      this.input.removeAttribute('aria-invalid');
      rememberPostal(this.postal);
      this.input.value = displayPostal(this.postal);
      this.update();
    }, options);
    this.input.addEventListener('input', () => { this.request?.abort(); this.requestId = (this.requestId || 0) + 1; this.clearResult(); this.postal = ''; this.location.hidden = true; }, options);
    this.querySelector('[data-change]').addEventListener('click', () => { this.form.hidden = false; this.input.focus(); this.input.select(); }, options);
    this.productRoot = this.closest('product-info');
    this.productRoot?.addEventListener('change', event => {
      if (event.target.name === 'id') { this.variantId = event.target.value; this.pendingVariant = false; this.update(); }
      else if (event.target.closest('variant-selects')) { this.request?.abort(); this.requestId = (this.requestId || 0) + 1; this.pendingVariant = true; this.dispatch.hidden = true; this.showFeedback(this.copy.loading); }
    }, options);
    document.addEventListener('visibilitychange', () => { if (!document.hidden) this.update(); }, options);
    try { this.config = await loadRoutes(); } catch { this.showFeedback(this.copy.unavailable); return; }
    if (!this.isConnected) return;
    const requested = this.preview ? new URL(location.href).searchParams.get('delivery_postal') : '';
    this.postal = normalizePostal(requested || storedPostal() || this.dataset.customerPostal);
    if (!validPostal(this.postal)) this.postal = '';
    this.input.value = displayPostal(this.postal);
    this.update();
    this.interval = setInterval(() => { if (!document.hidden) this.update(); }, 60000);
  }
  disconnectedCallback() { clearInterval(this.interval); this.controller?.abort(); this.request?.abort(); this.initialized = false; }
  clearResult() { this.arrival.hidden = true; this.arrival.textContent = ''; this.previewLabel.hidden = true; this.feedback.hidden = true; this.prompt.hidden = false; }
  showFeedback(message) { this.clearResult(); this.prompt.hidden = true; this.feedback.textContent = message; this.feedback.hidden = false; }
  displayDispatch(info) {
    if (!info) { this.dispatch.textContent = this.copy.static_dispatch; return; }
    this.dispatch.textContent = info.sameDay
      ? replace(this.copy.today, { hours: Math.floor(info.remainingMinutes / 60), minutes: info.remainingMinutes % 60 })
      : replace(this.copy.next, { date: formatDate(info.date, this.locale) });
  }
  async update() {
    if (!this.config || !this.isConnected) return;
    this.request?.abort();
    const requestId = this.requestId = (this.requestId || 0) + 1;
    const now = new Date();
    this.clearResult();
    const variant = this.variants?.find(value => String(value.id) === String(this.variantId));
    if (this.pendingVariant) { this.showFeedback(this.copy.loading); this.dispatch.hidden = true; return; }
    if (this.variants && (!variant?.available || variant.backorder)) {
      this.showFeedback(variant?.backorder ? this.copy.backorder : this.copy.sold_out);
      this.dispatch.hidden = true;
      this.form.hidden = true;
      this.location.hidden = true;
      return;
    }
    this.dispatch.hidden = false;
    this.displayDispatch(dispatchEstimate(now, this.config));
    if (!this.postal) { this.form.hidden = false; this.location.hidden = true; return; }
    this.location.hidden = false;
    this.querySelector('[data-destination]').textContent = replace(this.copy.to, { postal: displayPostal(this.postal) });
    let result;
    if (this.preview) result = deliveryEstimate(this.postal, now, this.config);
    else if (['main', 'unpublished'].includes(this.dataset.themeRole) && /^[1-9]\d{0,19}$/.test(this.dataset.themeId || '') && /^\/apps\/[a-zA-Z0-9/_-]+$/.test(this.dataset.endpoint)) {
      this.showFeedback(this.copy.loading);
      const request = this.request = new AbortController();
      const timer = setTimeout(() => request.abort(), 7000);
      try {
        const url = new URL(this.dataset.endpoint, location.origin);
        url.searchParams.set('postal_code', this.postal);
        url.searchParams.set(this.dataset.themeRole === 'main' ? 'theme_id' : 'preview_theme_id', this.dataset.themeId);
        if (variant) url.searchParams.set('variant_id', variant.id);
        const response = await fetch(url, { credentials: 'same-origin', signal: request.signal, headers: { Accept: 'application/json' } });
        if (!response.ok) throw new Error('Estimate unavailable');
        result = await response.json();
        const validDate = value => /^\d{4}-\d{2}-\d{2}$/.test(value) && Number.isFinite(Date.parse(`${value}T12:00:00Z`)) && new Date(`${value}T12:00:00Z`).toISOString().slice(0,10) === value;
        const currentDispatch = dispatchEstimate(now, this.config);
        if (result.status === 'ok' && (!validDate(result.deliveryDate) || !validDate(result.dispatchDate) || normalizePostal(result.postalCode) !== this.postal || result.deliveryDate < result.dispatchDate || (currentDispatch && result.dispatchDate < currentDispatch.date) || result.carrier !== 'GLS' || Math.abs(now.getTime() - Date.parse(result.quotedAt)) > 120000 || !Number.isFinite(Date.parse(result.quotedAt)))) throw new Error('Invalid estimate');
      } catch { result = { status: 'unavailable' }; }
      finally { clearTimeout(timer); }
    } else result = { status: 'unavailable' };
    if (requestId !== this.requestId || !this.isConnected) return;
    if (result.status !== 'ok') {
      this.showFeedback(this.preview && result.status === 'unavailable' ? this.copy.preview_unavailable : this.copy.unavailable);
      this.form.hidden = false;
      return;
    }
    this.clearResult();
    this.prompt.hidden = true;
    this.arrival.textContent = formatDate(result.deliveryDate, this.locale);
    this.arrival.hidden = false;
    this.previewLabel.hidden = this.dataset.themeRole !== 'unpublished';
    this.displayDispatch({ date: result.dispatchDate, sameDay: result.sameDay === true, remainingMinutes: result.remainingMinutes || 0 });
    this.form.hidden = true;
  }
}
if (!customElements.get('ab-delivery-date')) customElements.define('ab-delivery-date', ABDeliveryDate);
